import { HttpContext } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';

import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';

import { SKIP_LOADER } from '../../../mps/interceptors/skip-loader.token';
import { MpsApiService } from '../../../mps/services/mps-api.service';
import { applicationEndpoints } from '../../application-endpoints';

// ─── In-app notification interfaces ─────────────────────────────────────────

export interface NotificationMessageParams {
  contractor?: string;
  igg?: string;
  date?: string;
  reason?: string;
  total?: number;
  completed?: number;
  failed?: number;
}

export interface AppNotification {
  id: string;
  type: string;
  data: {
    // New structured payload (new notifications)
    message_key?: string;
    message_params?: NotificationMessageParams;
    reason_key?: string;
    // Legacy pre-rendered fields (old notifications — used as fallback)
    contractor_name?: string;
    igg?: string;
    outcome?: string;
    requested_end_date?: string;
    message?: string;
    title?: string;
    batch_id?: string;
    total?: number;
    completed?: number;
    failed?: number;
  };
  read_at: string | null;
  created_at: string;
}

export interface NotificationsResponse {
  data: AppNotification[];
}

/**
 * In-app notification service — fetches notifications on demand and caches them as signals.
 * Polling is handled by the NotificationBellComponent via interval + takeUntilDestroyed.
 *
 * @remarks
 * - Does NOT start polling itself to avoid DI issues in tests
 * - Call loadNotifications() to trigger a fetch (used on each poll tick)
 * - markAsRead() updates local state optimistically
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly _apiService = inject(MpsApiService);

  private readonly _normalIntervalMs = 120_000;
  private readonly _burstIntervalMs = 10_000;
  private readonly _burstDurationMs = 60_000;
  private _burstTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Current polling interval — consumers switch via switchMap when this emits */
  readonly pollInterval$ = new BehaviorSubject<number>(this._normalIntervalMs);

  /** All notifications (most recent first) */
  readonly notifications = signal<AppNotification[]>([]);

  /** Unread notification count (for bell badge) — derived from notifications */
  readonly unreadCount = computed(() => this.notifications().filter((n) => !n.read_at).length);

  /** Whether the notification dropdown is open */
  readonly isDropdownOpen = signal(false);

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Switches to burst polling (every 10s) for 60 seconds, then reverts to normal (every 2min).
   * Calling again mid-burst resets the 60s window.
   */
  triggerBurstPolling(): void {
    if (this._burstTimeout !== null) {
      clearTimeout(this._burstTimeout);
    }
    this.pollInterval$.next(this._burstIntervalMs);
    this._burstTimeout = setTimeout(() => {
      this.pollInterval$.next(this._normalIntervalMs);
      this._burstTimeout = null;
    }, this._burstDurationMs);
  }

  /** Fetch notifications and update signals */
  loadNotifications(): Observable<AppNotification[]> {
    return this._apiService
      .get<NotificationsResponse>(applicationEndpoints.notifications, {
        context: new HttpContext().set(SKIP_LOADER, true),
      })
      .pipe(
        map((response) => response.data ?? []),
        catchError(() => of([] as AppNotification[]))
      );
  }

  /** Update internal signals from fetched notifications */
  setNotifications(notifications: AppNotification[]): void {
    this.notifications.set(notifications);
  }

  /** Toggle notification dropdown visibility */
  toggleDropdown(): void {
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  /** Close dropdown */
  closeDropdown(): void {
    this.isDropdownOpen.set(false);
  }

  /**
   * Mark a notification as read (optimistic update + API call).
   * The Observable completes when the API call finishes.
   */
  markAsRead(notificationId: string): Observable<void> {
    // Optimistic update
    this.notifications.update((list) =>
      list.map((n) => (n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n))
    );

    return this._apiService
      .patch<void, void>(applicationEndpoints.notificationsMarkRead.replace(':id', notificationId), undefined, {
        context: new HttpContext().set(SKIP_LOADER, true),
      })
      .pipe(catchError(() => of(undefined as void)));
  }

  /** Mark all notifications as read (optimistic update + API call) */
  markAllAsRead(): void {
    const now = new Date().toISOString();
    this.notifications.update((list) => list.map((n) => ({ ...n, read_at: n.read_at ?? now })));
    this._apiService
      .patch<void, void>(applicationEndpoints.notificationsMarkAllRead, undefined, {
        context: new HttpContext().set(SKIP_LOADER, true),
      })
      .subscribe();
  }
}
